import { useEffect, useLayoutEffect, useRef } from "react";

import type { EventFilterScope } from "@/features/events/types/event-filter-scope";

import {
	useScopedEventsDispatch,
	useScopedEventsState,
} from "@/context/event-filters-registry";
import {
	hydrateEventsFilters,
	resetEventsFiltersToDefaults,
	updateEventsSeverityFilter,
} from "@/context/events";
import { DEFAULT_SEVERITY_FILTER } from "@/features/events/utils/severity-filter-utils";
import {
	clearAllStoredEventFilters,
	readStoredEventFilters,
	writeStoredEventFilters,
} from "@/features/events/utils/stored-events-filters";
import { useLocationZoneSensorIdSets } from "@/hooks/useOrgLocationTree";
import { useAuth } from "@/providers/auth-provider";

export type PersistedEventFiltersOptions = {
	enabled?: boolean;
	scope: EventFilterScope;
};

/** Persists event filters to org-scoped, page-specific localStorage. */
export function usePersistedEventsFilters({
	enabled = true,
	scope,
}: PersistedEventFiltersOptions) {
	const { isAuthenticated, orgCode } = useAuth();
	const idSets = useLocationZoneSensorIdSets();
	const {
		dateRangeFilter,
		eventTypesFilter,
		locationsFilter,
		searchQueryFilter,
		sensorsFilter,
		severityFilter,
		statusFilter,
		zonesFilter,
	} = useScopedEventsState(scope);
	const dispatch = useScopedEventsDispatch(scope);

	const isReadyRef = useRef(false);
	const skipNextSaveRef = useRef(false);
	const wasEnabledRef = useRef(false);
	const wasAuthenticatedRef = useRef(isAuthenticated);
	// Read at write time so a tree refetch doesn't re-run the save effects.
	const idSetsRef = useRef(idSets);
	useLayoutEffect(() => {
		idSetsRef.current = idSets;
	});

	useEffect(() => {
		if (wasAuthenticatedRef.current && !isAuthenticated) {
			clearAllStoredEventFilters();
			isReadyRef.current = false;
			skipNextSaveRef.current = true;
			wasEnabledRef.current = false;
			resetEventsFiltersToDefaults(dispatch, idSets);
		}
		wasAuthenticatedRef.current = isAuthenticated;
	}, [dispatch, idSets, isAuthenticated]);

	useLayoutEffect(() => {
		if (!orgCode) return;

		const enteringPage = !wasEnabledRef.current && enabled;
		const leavingPage = wasEnabledRef.current && !enabled;

		if (leavingPage && isReadyRef.current) {
			writeStoredEventFilters(
				orgCode,
				scope,
				{
					dateRangeFilter,
					eventTypesFilter,
					locationsFilter,
					searchQueryFilter,
					sensorsFilter,
					severityFilter,
					statusFilter,
					zonesFilter,
				},
				idSetsRef.current,
			);
		}

		if (enteringPage) {
			const stored = readStoredEventFilters(orgCode, scope);

			if (stored) {
				skipNextSaveRef.current = true;
				hydrateEventsFilters(dispatch, stored);
			} else if (severityFilter === null) {
				skipNextSaveRef.current = true;
				updateEventsSeverityFilter(dispatch, DEFAULT_SEVERITY_FILTER);
			}

			isReadyRef.current = true;
		}

		wasEnabledRef.current = enabled;
	}, [
		dateRangeFilter,
		dispatch,
		enabled,
		eventTypesFilter,
		locationsFilter,
		orgCode,
		searchQueryFilter,
		sensorsFilter,
		severityFilter,
		scope,
		statusFilter,
		zonesFilter,
	]);

	useEffect(() => {
		if (!enabled || !orgCode || !isReadyRef.current) return;

		if (skipNextSaveRef.current) {
			skipNextSaveRef.current = false;
			return;
		}

		writeStoredEventFilters(
			orgCode,
			scope,
			{
				dateRangeFilter,
				eventTypesFilter,
				locationsFilter,
				searchQueryFilter,
				sensorsFilter,
				severityFilter,
				statusFilter,
				zonesFilter,
			},
			idSetsRef.current,
		);
	}, [
		dateRangeFilter,
		enabled,
		eventTypesFilter,
		locationsFilter,
		orgCode,
		searchQueryFilter,
		sensorsFilter,
		severityFilter,
		scope,
		statusFilter,
		zonesFilter,
	]);
}
